/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { ISchemaConstStringModel } from "@adapptio/lib-blueprint";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";
import { FieldGroup, FieldText, InputAdornment } from "@adapptio/lib-editor-ui";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { termsEditorInspector } from "../../../../terms/inspector";

import "./InspectorTitle.scss";

export interface IInspectorItemIdProps {
	model?: ISchemaConstStringModel;
	label?: "id" | "name" | string;
}

export const InspectorTitle: React.FC<IInspectorItemIdProps> = ({ model: _model, label }) => {
	const t = useTranslate();
	const { model, validationErrors } = useBlueprintModel(_model, "constString");
	const inspectorContext = useEditorInspectorContext();

	const baseClass = BEM("edt-inspector-title");

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	const labelText =
		label === "id" || label === "name" ? t("editor", termsEditorInspector.component.name.label) : label;

	return (
		<FieldGroup grow>
			<FieldText
				key={model.nodeId}
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow
				readOnly={inspectorContext?.readOnly}
				debounceTimeout={inspectorContext?.inputDebounceTimeout}
				startAdornment={
					<InputAdornment center square>
						<span className={baseClass.element("label").toClassName()}>{labelText}</span>
					</InputAdornment>
				}
			/>
		</FieldGroup>
	);
};

InspectorTitle.defaultProps = {
	label: "id"
};
